import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import axios from '../../utils/axiosConfig';
import { setAuthToken } from '../../utils/auth';
import '../../styles/GeneralDetails.css';
import { InfoTooltip } from '../Common/InfoTooltip';

function GeneralDetails() {
  const [vendorDetails, setVendorDetails] = useState({
    business_name: '',
    phone: '',
    whatsapp_number: '',
    address: '',
    logo_url: '',
    cover_url: ''
  });
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [redemptionSettings, setRedemptionSettings] = useState({
    customerRedemptionEnabled: false,
    customerRedemptionPin: ''
  });
  const [toast, setToast] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);


  const debouncedFetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      try {
        await fetchVendorDetails();
        await fetchVendorSlides();
        await fetchRedemptionSettings();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
    
    debouncedFetchData();
    
    return () => {
      debouncedFetchData.cancel();
    };
  }, [debouncedFetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchVendorDetails = async () => {
    try {
      const response = await axios.get('/api/vendors/profile');
      setVendorDetails(prevDetails => ({
        ...prevDetails,
        ...response.data,
        logo_url: response.data.logo_url || '',
        cover_url: response.data.cover_url || ''
      }));
    } catch (error) {
      console.error('Error fetching vendor details:', error);
    }
  };

  const fetchVendorSlides = async () => {
    try {
      const response = await axios.get('/api/vendors/slides');
      if (Array.isArray(response.data)) {
        setSlides(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
        setSlides([]);
      }
    } catch (error) {
      console.error('Error fetching vendor slides:', error.response?.data || error.message);
      setSlides([]);
    }
  };
  
  const fetchRedemptionSettings = async () => {
    try {
      const response = await axios.get('/api/vendors/redemption-settings');
      setRedemptionSettings({
        customerRedemptionEnabled: response.data.customerRedemptionEnabled || false,
        customerRedemptionPin: response.data.customerRedemptionPin || ''
      });
    } catch (error) {
      console.error('Error fetching redemption settings:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!vendorDetails.business_name || vendorDetails.business_name.trim() === '') {
      errors.business_name = 'Business name is required';
    }
    
    if (!vendorDetails.phone || vendorDetails.phone.trim() === '') {
      errors.phone = 'Phone number is required';
    }
    
    if (!vendorDetails.address || vendorDetails.address.trim() === '') {
      errors.address = 'Address is required';
    }
    
    if (redemptionSettings.customerRedemptionEnabled) {
      const pin = redemptionSettings.customerRedemptionPin;
      if (!pin || pin.length < 4 || pin.length > 6 || !/^\d+$/.test(pin)) {
        errors.customerRedemptionPin = 'PIN must be 4-6 digits';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVendorDetails(prevDetails => ({
      ...prevDetails,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files[0]) {
      setVendorDetails(prevDetails => ({
        ...prevDetails,
        [name]: files[0]
      }));
    }
  };

  const handleRedemptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRedemptionSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

      // Prevent submission if it's from a tooltip click
    if (e.target.closest('.info-tooltip-container')) {
      return;
    }
      
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true); // <-- Start loader
    const formData = new FormData();
  
    Object.keys(vendorDetails).forEach(key => {
      if (vendorDetails[key] !== null && vendorDetails[key] !== undefined) {
        if (key === 'logo_url' || key === 'cover_url') {
          if (vendorDetails[key] instanceof File) {
            formData.append(key, vendorDetails[key]);
          }
        } else {
          formData.append(key, vendorDetails[key]);
        }
      }
    });
  
    try {
      const detailsResponse = await axios.put('/api/vendors/profile', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const redemptionResponse = await axios.put('/api/vendors/redemption-settings', redemptionSettings);
      
      setVendorDetails(prevDetails => ({
        ...prevDetails,
        ...detailsResponse.data
      }));
      
      setToast({
        type: 'success',
        message: 'Settings updated successfully'
      });
    }
    catch (error) {
      console.error('Error updating settings:', error);
      setToast({
        type: 'error',
        message: 'Error saving changes'
      });
    } finally {
      setIsSaving(false); // <-- Stop loader
    }
  };

  const handleAddSlide = () => {
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
  
      const formData = new FormData();
      formData.append('image', file);
  
      try {
        const response = await axios.post('/api/vendors/slides', formData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        await fetchVendorSlides();
        setToast({
          type: 'success',
          message: 'Slide added successfully'
        });
      } catch (error) {
        console.error('Error adding slide:', error);
        setToast({
          type: 'error',
          message: 'Error adding slide'
        });
      }
    };
    input.click();
  };

  const handleDeleteSlide = async (slideId) => {
    try {
      await axios.delete(`/api/vendors/slides/${slideId}`);
      setSlides(prevSlides => prevSlides.filter(slide => slide.id !== slideId));
      setToast({
        type: 'success',
        message: 'Slide deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting slide:', error);
      setToast({
        type: 'error',
        message: 'Error deleting slide'
      });
    }
  };

  if (isLoading) {
    return <div className="gd-loading">Loading...</div>;
  }

  return (
    <div className="gd-container">
      {toast && (
        <div className={`gd-toast ${toast.type}`}>
          <div className="gd-toast-content">
            <span className={`gd-toast-icon ${toast.type}`}>
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="gd-toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="gd-header">
        <div className="gd-header-content">
          <h1>Business Details</h1>
          <p className="gd-header-subtitle">Configure your business information that customers will see</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="gd-form">
        <div className="gd-card">
          <div className="gd-card-header">
            <h2>Basic Information</h2>
          </div>
          
          <div className="gd-card-body">
            <div className="gd-form-grid">
              <div className={`gd-form-group ${formErrors.business_name ? 'error' : ''}`}>
                <label className="gd-label" htmlFor="business_name">
                  Business Name <span className="gd-required">*</span>
                </label>
                <div className="gd-input-wrapper">
                  <input
                    type="text"
                    id="business_name"
                    name="business_name"
                    value={vendorDetails.business_name || ''}
                    onChange={handleInputChange}
                    required
                    className="gd-input"
                    placeholder="Enter business name"
                  />
                </div>
                {formErrors.business_name && (
                  <span className="gd-error-message">{formErrors.business_name}</span>
                )}
              </div>

              <div className={`gd-form-group ${formErrors.phone ? 'error' : ''}`}>
                <label className="gd-label" htmlFor="phone">
                  Phone Number <span className="gd-required">*</span>
                </label>
                <div className="gd-input-wrapper">
                  <span className="gd-input-icon">📱</span>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={vendorDetails.phone || ''}
                    onChange={handleInputChange}
                    required
                    className="gd-input with-icon"
                    placeholder="Enter phone number"
                  />
                </div>
                {formErrors.phone && (
                  <span className="gd-error-message">{formErrors.phone}</span>
                )}
              </div>

              <div className="gd-form-group">
                <label className="gd-label" htmlFor="whatsapp_number">WhatsApp Number</label>
                <div className="gd-input-wrapper">
                  <span className="gd-input-icon">📱</span>
                  <input
                    type="tel"
                    id="whatsapp_number"
                    name="whatsapp_number"
                    value={vendorDetails.whatsapp_number || ''}
                    onChange={handleInputChange}
                    className="gd-input with-icon"
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>

              <div className={`gd-form-group gd-full-width ${formErrors.address ? 'error' : ''}`}>
                <label className="gd-label" htmlFor="address">
                  Address <span className="gd-required">*</span>
                </label>
                <div className="gd-input-wrapper">
                  <textarea
                    id="address"
                    name="address"
                    value={vendorDetails.address || ''}
                    onChange={handleInputChange}
                    required
                    className="gd-input gd-textarea"
                    placeholder="Enter business address"
                    rows="3"
                  />
                </div>
                {formErrors.address && (
                  <span className="gd-error-message">{formErrors.address}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="gd-card">
          <div className="gd-card-header">
            <h2>Business Images</h2>
          </div>
          
          <div className="gd-card-body">
            <div className="gd-image-grid">
              <div className="gd-form-group">
                <label className="gd-label">Business Logo</label>
                <div className="gd-upload-wrapper" onClick={() => document.getElementById('logo_url').click()}>
                  {vendorDetails.logo_url ? (
                    <div className="gd-image-preview">
                      <img 
                        src={typeof vendorDetails.logo_url === 'string' 
                          ? vendorDetails.logo_url 
                          : URL.createObjectURL(vendorDetails.logo_url)} 
                        alt="Logo" 
                      />
                      <div className="gd-image-overlay">
                        <span>Change Image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="gd-upload-placeholder">
                      <div className="gd-upload-icon">+</div>
                      <span>Upload Logo</span>
                      <span className="gd-upload-hint">PNG, JPG up to 2MB</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="logo_url"
                    name="logo_url"
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <div className="gd-form-group">
                <label className="gd-label">Cover Image</label>
                <div className="gd-upload-wrapper" onClick={() => document.getElementById('cover_url').click()}>
                  {vendorDetails.cover_url ? (
                    <div className="gd-image-preview">
                      <img 
                        src={typeof vendorDetails.cover_url === 'string' 
                          ? vendorDetails.cover_url 
                          : URL.createObjectURL(vendorDetails.cover_url)} 
                        alt="Cover" 
                      />
                      <div className="gd-image-overlay">
                        <span>Change Image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="gd-upload-placeholder">
                      <div className="gd-upload-icon">+</div>
                      <span>Upload Cover</span>
                      <span className="gd-upload-hint">PNG, JPG up to 2MB</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="cover_url"
                    name="cover_url"
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="gd-card">
          <div className="gd-card-header">
            <h2>Redemption Settings</h2>
          </div>
          
          <div className="gd-card-body">
            <div className="gd-redemption-wrapper">
              <div className="gd-switch-group">
                <input
                  type="checkbox"
                  id="customerRedemptionEnabled"
                  name="customerRedemptionEnabled"
                  checked={redemptionSettings.customerRedemptionEnabled}
                  onChange={handleRedemptionChange}
                  className="gd-switch-input"
                />
                <label htmlFor="customerRedemptionEnabled" className="gd-switch-label">
                  <div className="gd-switch">
                    <div className="gd-switch-slider"></div>
                  </div>
                  <span className="gd-switch-text">Allow admin voucher and stamp redemption with PIN</span>
                </label>
              </div>

              {redemptionSettings.customerRedemptionEnabled && (
                <div className="gd-pin-container">
                  <div className="gd-form-group">
                    <label className="gd-label">PIN Code</label>
                    <div className="gd-input-wrapper">
                      <input
                        type="password"
                        name="customerRedemptionPin"
                        value={redemptionSettings.customerRedemptionPin}
                        onChange={handleRedemptionChange}
                        className={`gd-input gd-pin-input ${formErrors.customerRedemptionPin ? 'error' : ''}`}
                        placeholder="Enter 4-6 digit PIN"
                        maxLength={6}
                        required={redemptionSettings.customerRedemptionEnabled}
                      />
                    </div>
                    {formErrors.customerRedemptionPin ? (
                      <span className="gd-error-message">{formErrors.customerRedemptionPin}</span>
                    ) : (
                      <span className="gd-helper-text">This PIN will be used by your admin/staff to redeem vouchers and visits on the customer side</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="gd-card">
          <div className="gd-card-header">
            <h2>Promotional Slides
      
          <InfoTooltip 
            text="Upload promotional slides (2:1 ratio, min 800x400px) - shown to customers on first load" 
            variant="vendor"
            position="right"
            onClick={(e) => e.stopPropagation()}
          />
                
            </h2>
        
            <button type="button" onClick={handleAddSlide} className="gd-button secondary">
              Add Slide
            </button>
          </div>
          
          <div className="gd-card-body">
            {slides.length > 0 ? (
              <div className="gd-slides-grid">
                {slides.map((slide, index) => (
                  <div key={slide.id} className="gd-slide-item">
                    <div className="gd-slide-image">
                      <img src={slide.image_url} alt={`Slide ${index + 1}`} />
                      <div className="gd-slide-overlay">
                        <button 
                          onClick={() => handleDeleteSlide(slide.id)} 
                          className="gd-slide-delete"
                          type="button"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="gd-empty-state">
                <div className="gd-empty-icon">📸</div>
                <h3>No promotional slides</h3>
                <p>Add slides to showcase promotions and announcements to your customers</p>
                <button type="button" onClick={handleAddSlide} className="gd-button secondary">
                  Add First Slide
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="gd-form-actions">
        <button type="submit" className="gd-button primary" disabled={isSaving}>
          {isSaving ? (
            <>
              <span className="gd-spinner" /> Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
        </div>
      </form>
    </div>
  );
}

export default GeneralDetails;